import React, { useState, useEffect } from "react";
import {
	Table,
	Row,
	Col,
	Card,
	notification,
	Select,
	Spin,
	Tag,
	Badge,
	Tooltip,
} from "antd";

import {
	AppstoreAddOutlined,
	EyeOutlined,
	ScheduleOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";

import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import dashboardStatusService from "../../services/dashboardStatus";
import FormListComponent from "../../components/serviceDeskFormList";
import { useMediaQuery } from "react-responsive";
import { useSelector } from "react-redux";


const { Option } = Select;


const DashboardStatus = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { currentUser } = useSelector((state) => state.user);

	// 🔑 localStorage key (user-wise)
	const dstatusLocalKey = currentUser?.user_id
		? `dstatus_${currentUser.user_id}`
		: null;

	// 🔁 Load saved filters
	const getSavedFilters = () => {
		if (!dstatusLocalKey) return null;
		try {
			const saved = localStorage.getItem(dstatusLocalKey);
			return saved ? JSON.parse(saved) : null;
		} catch {
			return null;
		}
	};

	const savedFilters = getSavedFilters();

	const initialStatus =
		location.state?.statusFilter ||
		savedFilters?.statusFilter ||
		"Processing";

	const initialForm =
		location.state?.selectedForm || savedFilters?.selectedForm || null;

	const initialPage = savedFilters?.currentPage || 1;

	const [currentPage, setCurrentPage] = useState(initialPage);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [totalDisplay, setTotalDisplay] = useState("");
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [selectedForm, setSelectedForm] = useState(initialForm);
	const [statusFilter, setStatusFilter] = useState(initialStatus);

	const taskStatus = ["Approved", "Disapproved", "Return", "Processing"];

	// 💾 Save filters to localStorage
	const saveFilters = () => {
		if (!dstatusLocalKey) return;

		const payload = {
			statusFilter,
			selectedForm,
			currentPage,
		};

		localStorage.setItem(dstatusLocalKey, JSON.stringify(payload));
	};

	// 📦 Fetch data
	const getData = async () => {
		if (!statusFilter) return;

		setLoading(true);

		try {
			const response = await dashboardStatusService.getDashboardStatusVise(
				currentPage,
				statusFilter,
				selectedForm
			);

			if (response.success) {
				const items = response.items || [];
				const meta = response.meta || {};

				setData(items);

				const perPage = Number(meta.per_page) || items.length || 10;
				setItemsPerPage(perPage);

				const current = Number(meta.current_page) || currentPage || 1;
				if (meta.current_page) {
					setCurrentPage(current);
				}

				const total =
					meta.total !== undefined && meta.total !== null && meta.total !== ""
						? Number(meta.total)
						: items.length;

				setTotalDataCount(total);
				setTotalDisplay(meta.total_display || String(total));
			} else {
				setData([]);
				setTotalDataCount(0);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description: error?.response?.data?.message || "Something went wrong",
			});
			setData([]);
			setTotalDataCount(0);
		} finally {
			setLoading(false);
		}
	};

	// 🔁 Reset + fetch on filter change
	useEffect(() => {
		setData([]);
		setCurrentPage(1);
	}, [statusFilter, selectedForm]);

	// 🔁 Fetch on page/filter change
	useEffect(() => {
		getData();
		saveFilters(); // 💾 persist on every change
		// eslint-disable-next-line
	}, [currentPage, statusFilter, selectedForm]);

	const columns = [
		{
			title: "Form",
			dataIndex: "form_name",
			key: "form_name",
			width: "15%",
		},
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			width: "15%",
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			width: "50%",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: "10%",
			render: (status) => (
				<Tag
					color={
						(status === "Processing" && "blue") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red") ||
						(status === "Pending" && "orange")
					}
				>
					{status}
				</Tag>
			),
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
		},
		{
			title: "Logs",
			dataIndex: "logs",
			key: "logs",
			width: "5%",
			render: (_, record) => (
				<Tooltip title="Activity Log">
					<span
						onClick={() =>
							navigate("/activity-log", {
								state: {
									form_id: record.form_id,
									key: record.key,
								},
							})
						}
						style={{ cursor: "pointer", marginLeft: 8 }}
					>
						<ScheduleOutlined style={{ color: "#1677ff" }} />
					</span>
				</Tooltip>
			),
		},
		{
			title: "Action",
			key: "action",
			width: "5%",
			render: (_, record) => (
				<Link to={`/${record.form_slug}/details/${record.key}`}>
					<EyeOutlined />
				</Link>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<AppstoreAddOutlined />} title="Status Overview" />
				</Col>
			</Row>

			<Card>
				<Row gutter={16} style={{ marginBottom: 16 }}>
					<Col span={6}>
						<Select
							showSearch
							style={{ width: "100%" }}
							size="large"
							placeholder="Please Select Task Status"
							value={statusFilter}
							onChange={(value) => {
								setStatusFilter(value);
							}}
							allowClear
						>
							{taskStatus.map((status) => (
								<Option key={status} value={status}>
									{status}
								</Option>
							))}
						</Select>
					</Col>

					<Col span={6}>
						<FormListComponent
							onChange={(value) => {
								setSelectedForm(value);
							}}
							selectedFormId={selectedForm}
						/>
					</Col>

					<Col span={12} align="right">
						<Badge count={totalDisplay} overflowCount={999}>
							<Card title="Total Records" style={{ border: "none" }} />
						</Badge>
					</Col>
				</Row>
			</Card>

			<Card>
				<Spin spinning={loading} tip="Loading..." size="large">
					<Table
						rowKey="record_id"
						columns={columns}
						dataSource={data}
						pagination={{
							current: currentPage,
							pageSize: itemsPerPage,
							total: totalDataCount,
							onChange: (page) => setCurrentPage(page),
						}}
						scroll={{ y: 400 }}
					/>
				</Spin>
			</Card>
		</DefaultLayout>
	);
};

export default DashboardStatus;